/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import AreaBase from '@fusioncharts/charts/src/chart/_internal/areabase';
import axisFactory from '../../factories/multiaxis-axis';
import vCanvasFactory from '../../factories/multiaxis-vcanvas';
import datasetFactory from '../../factories/multiaxis-dataset';
import { getDep } from '@fusioncharts/core/src/dependency-manager';
import raphaelShapesButton from '@fusioncharts/core/src/_internal/redraphael/redraphael-shapes/redraphael-shapes.button';

import { pluckNumber, extend2, pluck, chartPaletteStr, preDefStr, POSITION_TOP, POSITION_LEFT, POSITION_RIGHT, pluckFontSizeMaintainUnit } from '@fusioncharts/core/src/lib';
import isEqualString from '@fusioncharts/utils/src/string/equal';

const MULTIAXISLINE = 'multiaxisline';
let defaultFontStr = preDefStr.defaultFontStr,
  divLineAlphaStr = preDefStr.divLineAlphaStr,
  altVGridColorStr = preDefStr.altVGridColorStr,
  altVGridAlphaStr = preDefStr.altVGridAlphaStr,
  COLOR_000000 = preDefStr.colors.c000000,
  POSITION_BOTTOM = preDefStr.POSITION_BOTTOM,
  divLineAlpha3DStr = preDefStr.divLineAlpha3DStr,
  Raphael = getDep('redraphael', 'plugin');

// Resolve dependency to draw button shape
raphaelShapesButton(Raphael);

/**
 * Class for MultiAxisLine chartAPI
 */
class MultiAxisLine extends AreaBase {
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'MultiAxisLine';
  }

  /**
   * Constructor function of MultiAxisLine chartAPI
   */
  constructor () {
    super();
    var iapi = this;
    iapi.defaultPlotShadow = 1;
    iapi.axisPaddingLeft = 0;
    iapi.axisPaddingRight = 0;
    this.registerFactory('axis', axisFactory, ['canvas']);
    this.registerFactory('vCanvas', vCanvasFactory, ['axis']);
    this.registerFactory('dataset', datasetFactory, ['vCanvas']);
  }
  /**
   * This sets default configuration
   * @memberof MultiAxisLine
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.defaultDatasetType = MULTIAXISLINE;
    config.defaultcrosslinethickness = 1;
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'MultiAxisLine';
  }
  /**
   * function to check if the chart specific data is proper is not
   * this fn is define for specific chart types
   * @return {boolean} if JSON data is valid or not
   */
  _checkInvalidSpecificData () {
    let jsonData = this.getFromEnv('dataSource');
    if (!jsonData.axis || !jsonData.categories) {
      return true;
    }
  }

  /**
   * Function to configure the axis
   * @return {Object} configurations of axes
   */
  _feedAxesRawData () {
    var iapi = this,
      chartConfig = iapi.config,
      colorM = iapi.getFromEnv('color-manager'),
      numberFormatter = iapi.getFromEnv('number-formatter'), // eslint-disable-line good-practices/no-single-usage-variable
      dataObj = iapi.getFromEnv('dataSource'),
      chartAttrs = dataObj.chart,
      xAxisConf,
      yAxisConf,
      is3D = iapi.config.is3D,
      palleteString = is3D ? chartPaletteStr.chart3D : chartPaletteStr.chart2D,
      outCanfontSizeWithUnit = pluckFontSizeMaintainUnit(chartAttrs.outcnvbasefontsize, chartAttrs.basefontsize, 10),
      axes,
      i,
      len,
      isOpposit,
      axisJson,
      plotColor,
      leftAxes,
      rightAxes,
      axisHEXColor,
      gridLineWidth,
      tickWidth,
      axisLineThickness,
      yAxisConfigure = [],
      setAxisConfig = {},
      xAxisConfigure = [],
      j,
      jlen;

    xAxisConf = { // eslint-disable-line good-practices/no-single-usage-variable
      isVertical: false,
      isReverse: false,
      isOpposit: isEqualString(chartAttrs.xaxisposition, 'top'),
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#? ([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.xaxisnamepadding,
      axisValuePadding: chartAttrs.labelpadding,
      axisNameFont: chartAttrs.xaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.xaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.xaxisnamefontsize,
      axisNameFontColor: chartAttrs.xaxisnamefontcolor,
      axisNameFontBold: chartAttrs.xaxisnamefontbold,
      axisNameFontItalic: chartAttrs.xaxisnamefontitalic,
      axisNameBgColor: chartAttrs.xaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.xaxisnamebordercolor,
      axisNameAlpha: chartAttrs.xaxisnamealpha,
      axisNameFontAlpha: chartAttrs.xaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.xaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.xaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.xaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.xaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.xaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.xaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.xaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.xaxisnameborderdashgap,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      divLineColor: pluck(chartAttrs.vdivlinecolor, chartAttrs.divlinecolor,
        colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.vdivlinealpha, chartAttrs.divlinealpha,
        is3D ? colorM.getColor(divLineAlpha3DStr) : colorM.getColor(divLineAlphaStr)),
      divLineThickness: pluckNumber(chartAttrs.vdivlinethickness, chartAttrs.divlinethickness, 1),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.vdivlinedashed, chartAttrs.vdivlineisdashed,
        chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 0)),
      divLineDashLen: pluckNumber(chartAttrs.vdivlinedashlen, chartAttrs.divlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.vdivlinedashgap, chartAttrs.divlinedashgap, 2),
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatevgridcolor, 0),
      alternateGridColor: pluck(chartAttrs.alternatevgridcolor, colorM.getColor(altVGridColorStr)),
      alternateGridAlpha: pluck(chartAttrs.alternatevgridalpha, colorM.getColor(altVGridAlphaStr)),
      numDivLines: chartAttrs.numvdivlines,
      labelFont: chartAttrs.labelfont,
      labelFontSize: this.computeFontSize(chartAttrs.labelfontsize),
      labelFontSizeWithUnit: chartAttrs.labelfontsize,
      labelFontColor: chartAttrs.labelfontcolor,
      labelAlpha: chartAttrs.labelalpha,
      labelFontBold: chartAttrs.labelfontbold,
      labelFontItalic: chartAttrs.labelfontitalic,
      labelBdrColor: chartAttrs.labelbordercolor,
      labelBdrAlpha: chartAttrs.labelborderalpha,
      labelLink: chartAttrs.labellink,
      labelBgColor: chartAttrs.labelbgcolor,
      labelBgAlpha: chartAttrs.labelbgalpha,
      labelBdrThickness: chartAttrs.labelborderthickness,
      labelBdrPadding: chartAttrs.labelborderpadding,
      labelBdrRadius: chartAttrs.labelborderradius,
      labelBdrDashed: chartAttrs.labelborderdashed,
      labelBdrDashLen: chartAttrs.labelborderdashlen,
      labelBdrDashGap: chartAttrs.labelborderdashgap,
      maxLabelHeight: chartAttrs.maxlabelheight,
      axisName: chartAttrs.xaxisname,
      axisMinValue: chartAttrs.xaxisminvalue,
      axisMaxValue: chartAttrs.xaxismaxvalue,
      setAdaptiveMin: chartAttrs.setadaptivexmin,
      adjustDiv: chartAttrs.adjustvdiv,
      labelDisplay: chartAttrs.labeldisplay,
      showLabels: chartAttrs.showlabels,
      rotateLabels: chartAttrs.rotatelabels,
      slantLabel: pluckNumber(chartAttrs.slantlabels, chartAttrs.slantlabel),
      labelStep: pluckNumber(chartAttrs.labelstep, chartAttrs.xaxisvaluesstep),
      showAxisValues: pluckNumber(chartAttrs.showxaxisvalues, chartAttrs.showxaxisvalue),
      showLimits: chartAttrs.showvlimits,
      showDivLineValues: pluckNumber(chartAttrs.showvdivlinevalues, chartAttrs.showvdivlinevalues),
      showZeroPlane: chartAttrs.showvzeroplane,
      zeroPlaneColor: chartAttrs.vzeroplanecolor,
      zeroPlaneThickness: chartAttrs.vzeroplanethickness,
      zeroPlaneAlpha: chartAttrs.vzeroplanealpha,
      showZeroPlaneValue: chartAttrs.showvzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      showAxisLine: pluckNumber(chartAttrs.showxaxisline, chartAttrs.showaxislines,
        chartAttrs.drawAxisLines, 0),
      axisLineThickness: pluckNumber(chartAttrs.xaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.xaxislinealpha, chartAttrs.axislinealpha, 100),
      axisLineColor: pluck(chartAttrs.xaxislinecolor, chartAttrs.axislinecolor, COLOR_000000)
    };
    xAxisConfigure.push(xAxisConf);
    yAxisConf = {
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#? ([a-f0-9]+)/ig, '#$1'),
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      showAlternateGridColor: 0,
      axisNameFont: chartAttrs.yaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.yaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.yaxisnamefontsize,
      axisNameFontColor: chartAttrs.yaxisnamefontcolor,
      axisNameFontBold: chartAttrs.yaxisnamefontbold,
      axisNameFontItalic: chartAttrs.yaxisnamefontitalic,
      axisNameBgColor: chartAttrs.yaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.yaxisnamebordercolor,
      axisNameAlpha: chartAttrs.yaxisnamealpha,
      axisNameFontAlpha: chartAttrs.yaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.yaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.yaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.yaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.yaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.yaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.yaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.yaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.yaxisnameborderdashgap,
      labelFont: chartAttrs.yaxisvaluefont,
      labelFontSize: this.computeFontSize(chartAttrs.yaxisvaluefontsize),
      labelFontSizeWithUnit: chartAttrs.yaxisvaluefontsize,
      labelFontColor: chartAttrs.yaxisvaluefontcolor,
      labelAlpha: chartAttrs.yaxisvaluealpha,
      labelFontBold: chartAttrs.yaxisvaluefontbold,
      labelFontItalic: chartAttrs.yaxisvaluefontitalic,
      labelBdrColor: chartAttrs.yaxisvaluebordercolor,
      labelBdrAlpha: chartAttrs.yaxisvalueborderalpha,
      labelLink: chartAttrs.yaxisvaluelink,
      labelBgColor: chartAttrs.yaxisvaluebgcolor,
      labelBgAlpha: chartAttrs.yaxisvaluebgalpha,
      labelBdrThickness: chartAttrs.yaxisvalueborderthickness,
      labelBdrPadding: chartAttrs.yaxisvalueborderpadding,
      labelBdrRadius: chartAttrs.yaxisvalueborderradius,
      labelBdrDashed: chartAttrs.yaxisvalueborderdashed,
      labelBdrDashLen: chartAttrs.yaxisvalueborderdashlen,
      labelBdrDashGap: chartAttrs.yaxisvalueborderdashgap
    };

    leftAxes = {
      'side': 'l',
      axesConf: []
    };
    rightAxes = {
      'side': 'r',
      axesConf: []
    };

    axes = dataObj.axis || [];
    for (i = 0, len = axes.length; i < len; i++) { // eslint-disable-line good-practices/no-single-usage-variable
      setAxisConfig = {};
      axisJson = axes[i];
      setAxisConfig.checkBoxChecked = false;
      for (j = 0, jlen = axisJson.dataset ? axisJson.dataset.length : 0; j < jlen; j += 1) {
        if (Number(axisJson.dataset[j].visible) !== 0) {
          setAxisConfig.checkBoxChecked = true;
        }
      }
      // showAxis = pluckNumber(axisJson.showaxis, 1);
      plotColor = colorM.getPlotColor(i);
      axisHEXColor = pluck(axisJson.color, chartAttrs.axiscolor,
        plotColor);
      gridLineWidth = pluckNumber(axisJson.divlinethickness,
        chartAttrs.divlinethickness, 1);
      tickWidth = pluckNumber(axisJson.tickwidth, chartAttrs.axistickwidth, 2);
      axisLineThickness = pluckNumber(axisJson.axislinethickness, chartAttrs.axislinethickness, 2);
      isOpposit = !(pluckNumber(axisJson.axisonleft, 1));

      yAxisConf.isVertical = true;
      yAxisConf.isReverse = true;
      yAxisConf.isOpposit = isOpposit;
      // add axis configuration
      yAxisConf.labelStep = pluckNumber(axisJson.yaxisvaluesstep, axisJson.yaxisvaluestep,
        chartAttrs.yaxisvaluesstep, chartAttrs.yaxisvaluestep);
      yAxisConf.axisMaxValue = axisJson.maxvalue;
      // yAxisConf.tickWidth = tickWidth;
      yAxisConf.axisMinValue = axisJson.minvalue;
      yAxisConf.setAdaptiveMin = pluckNumber(axisJson.setadaptiveymin, chartAttrs.setadaptiveymin);
      yAxisConf.numDivLines = pluckNumber(axisJson.numdivlines, chartAttrs.numdivlines, 4);
      yAxisConf.adjustDiv = pluckNumber(axisJson.adjustdiv, chartAttrs.adjustdiv);
      yAxisConf.showAxisValues = pluckNumber(axisJson.showyaxisvalues,
        axisJson.showyaxisvalue, chartAttrs.showyaxisvalues, chartAttrs.showyaxisvalue, 1);
      yAxisConf.showLimits = pluckNumber(axisJson.showlimits, chartAttrs.showyaxislimits,
        chartAttrs.showlimits, yAxisConf.showAxisValues);
      yAxisConf.showDivLineValues = pluckNumber(axisJson.showdivlinevalue,
        chartAttrs.showdivlinevalues, axisJson.showdivlinevalues,
        yAxisConf.showAxisValues);
      yAxisConf.showZeroPlane = pluckNumber(axisJson.showzeroplane, chartAttrs.showzeroplane);
      yAxisConf.showZeroPlaneValue = pluckNumber(axisJson.showzeroplanevalue,
        chartAttrs.showzeroplanevalue);
      yAxisConf.zeroPlaneColor = axisJson.zeroplanecolor;
      yAxisConf.zeroPlaneThickness = pluckNumber(axisJson.zeroplanethickness, axisJson.divlinethickness,
        chartConfig.zeroplanethickness, 2);
      yAxisConf.zeroPlaneAlpha = pluckNumber(axisJson.zeroplanealpha, axisJson.divlinealpha,
        chartConfig.zeroplanealpha);
      yAxisConf.showZeroPlaneOnTop = chartConfig.showzeroplaneontop;
      yAxisConf.divLineColor = pluck(axisJson.divlinecolor, axisHEXColor);
      yAxisConf.divLineAlpha = pluckNumber(axisJson.divlinealpha, chartAttrs.divlinealpha,
        colorM.getColor(divLineAlphaStr), 100);
      yAxisConf.divLineThickness = gridLineWidth;
      yAxisConf.divLineIsDashed = Boolean(pluckNumber(axisJson.divlinedashed, axisJson.divlineisdashed,
        chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 0));
      yAxisConf.divLineDashLen = pluckNumber(axisJson.divlinedashlen,
        chartAttrs.divlinedashlen, 4);
      yAxisConf.divLineDashGap = pluckNumber(axisJson.divlinedashgap,
        chartAttrs.divlinedashgap, 2);

      yAxisConf.showAxisLine = 1;
      yAxisConf.axisLineThickness = axisLineThickness;
      yAxisConf.axisLineAlpha = 100;
      yAxisConf.axisLineColor = axisHEXColor;
      yAxisConf.tickLength = tickWidth;
      yAxisConf.tickColor = axisHEXColor;
      yAxisConf.tickAlpha = 100;
      yAxisConf.tickWidth = axisLineThickness;
      yAxisConf.axisName = axisJson.title;
      yAxisConf.rotateAxisName = 1;
      yAxisConf.axisNameAlignCanvas = true;
      yAxisConf.drawAxisNameFromBottom = true;

      extend2(setAxisConfig, yAxisConf);
      numberFormatter.parseMLAxisConf(axisJson, i);
      if (pluckNumber(axisJson.showaxis) === 0) {
        extend2(setAxisConfig, {
          axisIndex: i,
          drawAxisLineWRTCanvas: false,
          drawLabels: false,
          drawPlotlines: false,
          drawAxisLine: false,
          drawPlotBands: false,
          drawAxisName: false,
          drawTrendLines: false,
          drawTrendLabels: false,
          drawTick: false,
          drawTickMinor: false,
          showAxis: 0
        });
      } else {
        extend2(setAxisConfig, {
          axisIndex: i,
          drawAxisLineWRTCanvas: false,
          drawLabels: true,
          drawPlotlines: true,
          drawAxisLine: true,
          drawPlotBands: true,
          drawAxisName: true,
          drawTrendLines: true,
          drawTrendLabels: true,
          drawTick: true,
          drawTickMinor: true,
          showAxis: 1
        });
      }
      if (isOpposit) {
        rightAxes.axesConf.push(setAxisConfig);
      } else {
        leftAxes.axesConf.push(setAxisConfig);
      }
    }
    leftAxes.axesConf.length && yAxisConfigure.push(leftAxes);
    rightAxes.axesConf.length && yAxisConfigure.push(rightAxes);
    return { xAxisConfigure, yAxisConfigure };
  }
  /**
   * Managing axis and legend space management after the actual space management
   */
  _postSpaceManagement () {
    var iapi = this,
      legend = iapi.getFromEnv('legend'),
      legendPos = legend && legend.config && legend.config.legendPos ? legend.config.legendPos.split('-') : []; // eslint-disable-line good-practices/no-single-usage-variable

    iapi.setAxisDimention();
    // little space is required between legend and axis selector ui
    // when legend position is right-* or left-*
    if (legendPos[0] === POSITION_LEFT) {
      iapi._allocateSpace({ left: 5 });
    } else if (legendPos[0] === POSITION_RIGHT) {
      iapi._allocateSpace({ right: 5 });
    }
    // eslint-disable-next-line good-practices/no-function-dependency
    iapi.getChildren('yAxisSelectorUI').forEach(comp => {
      comp.setAxisDimention();
    });
    // function for adjusting value padding depending upon data and axis labels.
    iapi.getChildren('canvas')[0].setCanvasPadding();
    iapi.setAxisDimention();
    iapi.config.showLegend && legend.postSpaceManager();

    iapi.allocateDimensionOfChartMenuBar();
  }
  /**
   * Sets dimension of Axis components
   */
  setAxisDimention () {
    var iapi = this,
      config = iapi.config,
      xAxis = iapi.getChildren('xAxis')[0],
      xDepth = config.xDepth || 0,
      canvasConfig = iapi.getChildren('canvas')[0].config,
      canvasBorderWidth = canvasConfig.canvasBorderWidth,
      canvasPadding = canvasConfig.canvasPadding,
      canvasPaddingLeft = canvasConfig.canvasPaddingLeft,
      shift = config.shift || 0,
      canvasPaddingRight = canvasConfig.canvasPaddingRight; // eslint-disable-line good-practices/no-single-usage-variable
    xAxis && xAxis.setAxisDimention({
      x: config.canvasLeft + xDepth + Math.max(canvasPaddingLeft, canvasPadding),
      y: xAxis.config.isOpposit
        ? config.canvasTop - canvasBorderWidth
        : config.canvasBottom + shift + canvasBorderWidth,
      opposite: xAxis.config.isOpposit
        ? config.canvasBottom + shift + canvasBorderWidth
        : config.canvasTop - canvasBorderWidth,
      axisLength: config.canvasWidth - xDepth - Math.max(canvasPaddingLeft, canvasPadding) -
                              Math.max(canvasPaddingRight, canvasPadding)
    });
  }

  /**
   * Managing the space of all the components of the chart including all the axiss
   */
  _spaceManager () {
    // todo marge _allocateSpace and _spacemanager
    var spaceForActionBar,
      actionBarSpace,
      availableWidth,
      availableHeight,
      iapi = this,
      canvas = iapi.getChildren('canvas')[0],
      config = iapi.config,
      // axesArr = config.axesArr,
      // components = iapi.components,
      legendPosition = config.legendPosition ? config.legendPosition.split('-') : [],
      // axis = config.axisDataSetMap,
      xAxis = iapi.getChildren('xAxis'),
      // yAxis = iapi.getChildren('yAxis'),
      axisSelectorUI = iapi.getChildren('yAxisSelectorUI'),
      yDepth = config.yDepth,
      legend = iapi.getFromEnv('legend'), // eslint-disable-line good-practices/no-single-usage-variable
      // axesPadding = config.axesPadding,
      rightPadding = 0,
      leftPadding = 0,
      // length = yAxis.length,
      length = axisSelectorUI.length || 0, // eslint-disable-line good-practices/no-single-usage-variable
      // yAxisObj,
      i,
      yAxisSpaceUsed,
      // axesMap,
      // leftAxes,
      // rightAxes,
      allotedSpace,
      // mappedAxis,
      chartAttrs = iapi.getFromEnv('dataSource').chart,
      showBorder = pluckNumber(chartAttrs.showborder, iapi.config.is3D ? 0 : 1), // eslint-disable-line good-practices/no-single-usage-variable
      canvasBorderWidth = canvas.config.canvasBorderWidth,
      chartBorderWidth =
                  config.borderWidth = showBorder ? pluckNumber(chartAttrs.borderthickness, 1) : 0,
      canvasMarginTop = config.canvasMarginTop,
      canvasMarginBottom = config.canvasMarginBottom,
      canvasMarginLeft = config.canvasMarginLeft,
      canvasMarginRight = config.canvasMarginRight,
      minCanvasHeight = config.minCanvasHeight,
      minCanvasWidth = config.minCanvasWidth,
      height = config.height,
      width = config.width,
      diff,
      heightAdjust = false, // eslint-disable-line good-practices/no-single-usage-variable
      widthAdjust = false, // eslint-disable-line good-practices/no-single-usage-variable
      top,
      bottom,
      left,
      right,
      currentCanvasHeight,
      currentCanvasWidth,
      origCanvasTopMargin = config.origCanvasTopMargin,
      origCanvasBottomMargin = config.origCanvasBottomMargin,
      origCanvasLeftMargin = config.origCanvasLeftMargin,
      origCanvasRightMargin = config.origCanvasRightMargin,
      sum;
    //* ***** Manage space
    iapi._allocateSpace({
      top: chartBorderWidth,
      bottom: chartBorderWidth,
      left: chartBorderWidth,
      right: chartBorderWidth
    });

    iapi._allocateSpace({
      left: config.canvasMarginLeft,
      right: config.canvasMarginRight
    });
    //* ***** Manage space
    if (legendPosition[0] === 'left' || legendPosition[0] === 'right') {
      allotedSpace = config.canvasWidth * 0.3;
    } else {
      allotedSpace = config.canvasHeight * 0.3;
    }
    // No space is allocated for legend drawing in single series charts
    ((config.hasLegend !== false) && xAxis) && config.showLegend && iapi._allocateSpace(legend._manageLegendPosition(allotedSpace));

    // @todo uncomment this code
    for (i = 0; i < length; i++) {
      yAxisSpaceUsed = axisSelectorUI[i].placeAxis(config.canvasWidth);
      rightPadding += yAxisSpaceUsed.rightPadding;
      leftPadding += yAxisSpaceUsed.leftPadding;
      iapi._allocateSpace(yAxisSpaceUsed.dimension);
    }
    spaceForActionBar = config.availableHeight * 0.225; // eslint-disable-line good-practices/no-single-usage-variable
    actionBarSpace = (iapi._manageActionBarSpace && iapi._manageActionBarSpace(spaceForActionBar)) || {}; // eslint-disable-line good-practices/no-single-usage-variable
    iapi._allocateSpace(actionBarSpace);

    availableHeight = (legendPosition[0] === POSITION_TOP || legendPosition[0] === POSITION_BOTTOM) ? config.canvasHeight * 0.6
      : config.canvasWidth * 0.6;
    iapi._manageChartMenuBar(availableHeight);
    availableWidth = config.canvasWidth * 0.7; // eslint-disable-line good-practices/no-single-usage-variable
    if (availableWidth > (rightPadding + leftPadding)) {
      iapi._allocateSpace({
        'left': leftPadding,
        'right': rightPadding
      });
    }
    availableHeight = config.canvasHeight * 0.6;
    xAxis[0] && iapi._allocateSpace(xAxis[0].placeAxis(availableHeight));
    // alocate the space for datasets
    availableHeight = config.canvasHeight * 0.325;
    iapi._getDSspace && iapi._allocateSpace(iapi._getDSspace(availableHeight));

    if (yDepth) {
      iapi._allocateSpace({
        bottom: yDepth
      });
      // shift = xDepth + canvasBasePadding + canvasBaseDepth;
    }

    iapi._allocateSpace({
      top: canvasBorderWidth,
      bottom: canvasBorderWidth,
      left: canvasBorderWidth,
      right: canvasBorderWidth
    });
    // Allocate space for canvas margin only if the margin is less than the margin entered by the user.
    top = canvasMarginTop > config.canvasTop ? (canvasMarginTop - config.canvasTop) : 0;
    bottom = canvasMarginBottom > (height - config.canvasBottom)
      ? (canvasMarginBottom + config.canvasBottom - height) : 0;
    left = canvasMarginLeft > config.canvasLeft ? (canvasMarginLeft - config.canvasLeft) : 0;
    right = canvasMarginRight > (width - config.canvasRight)
      ? (canvasMarginRight + config.canvasRight - width) : 0;

    iapi._allocateSpace({
      top: top,
      bottom: bottom,
      left: left,
      right: right
    });
    // Forcing canvas height to its minimum
    if (heightAdjust) {
      sum = origCanvasTopMargin + origCanvasBottomMargin;
      currentCanvasHeight = config.canvasHeight;
      if (currentCanvasHeight > minCanvasHeight) {
        diff = currentCanvasHeight - minCanvasHeight;
        top = diff * origCanvasTopMargin / sum;
        bottom = diff * origCanvasBottomMargin / sum;
      }
      iapi._allocateSpace({
        top: top,
        bottom: bottom
      });
    }

    // Forcing canvas width to its minimum
    if (widthAdjust) {
      sum = origCanvasLeftMargin + origCanvasRightMargin;
      currentCanvasWidth = config.canvasWidth;
      if (currentCanvasWidth > minCanvasWidth) {
        diff = currentCanvasWidth - minCanvasWidth;
        left = diff * origCanvasLeftMargin / sum;
        right = diff * origCanvasRightMargin / sum;
      }
      iapi._allocateSpace({
        left: left,
        right: right
      });
    }

    config.actualCanvasMarginTop = top;
    config.actualCanvasMarginLeft = left;
    config.actualCanvasMarginRight = right;
    config.actualCanvasMarginBottom = bottom;

    canvas.setDimension({
      top: config.canvasTop,
      left: config.canvasLeft,
      width: config.canvasWidth,
      height: config.canvasHeight
    });
  }

  /**
   * Function to parse some additional parameters of the chart
   * @param  {Object} dataObj contains json data of the chart
   */
  configure (dataObj) {
    super.configure(dataObj);
    var iapi = this,
      conf = iapi.config,
      chartAttr = iapi.getFromEnv('dataSource').chart;

    conf.axesPadding = 5;
    conf.allowAxisShift = pluckNumber(chartAttr.allowaxisshift, 1);
    conf.allowSelection = pluckNumber(chartAttr.allowselection, 1);
    conf.checkBoxColor = pluck(chartAttr.checkboxcolor, '#2196f3');
    conf.axisConfigured = true;
  }
}
export default MultiAxisLine;
